import { useState } from "react";
import { KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };
const FIELDS = [
  ["current_password", "Current password", "change-password-current-input"],
  ["new_password", "New password", "change-password-new-input"],
  ["confirm_password", "Confirm new password", "change-password-confirm-input"],
];

export const ChangePassword = () => {
  const { setUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const { data } = await api.post("/auth/change-password", form);
      setUser(data.user);
      setForm(EMPTY);
      setStatus({ ok: true, msg: data.message });
    } catch (err) {
      setStatus({ ok: false, msg: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="change-password-section" className="glass hairline rounded-2xl border p-6">
      <h2 className="flex items-center gap-2 text-xl font-medium"><KeyRound className="h-5 w-5 text-[#E9C98B]" />Change password</h2>
      <p className="mt-1 text-sm text-muted-foreground">At least 10 characters. Changing it signs you out on your other devices.</p>
      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="change-password-form">
        {FIELDS.map(([k, label, id]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            <Input id={id} type="password" autoComplete={k === "current_password" ? "current-password" : "new-password"}
              data-testid={id} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </div>
        ))}
        {status && (
          <p data-testid={status.ok ? "change-password-success" : "change-password-error"}
            className={`text-sm ${status.ok ? "text-emerald-300" : "text-rose-300"}`}>{status.msg}</p>
        )}
        <Button type="submit" disabled={busy} data-testid="change-password-submit-button" className="btn-aurora rounded-full px-6">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </section>
  );
};

export const DefaultPasswordBanner = () => {
  const { user } = useAuth();
  if (user?.role !== "admin" || !user.uses_default_password) return null;
  return (
    <div data-testid="admin-default-password-banner" className="border-b border-rose-300/30 bg-rose-500/10 text-rose-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-sm sm:px-8">
        <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" />Change your admin password before inviting beta sellers.</span>
        <a href="/app/settings#change-password" data-testid="admin-default-password-link" className="inline-flex min-h-[44px] items-center underline underline-offset-4 hover:text-white">Change it in Settings</a>
      </div>
    </div>
  );
};
